import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, typography, spacing } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { ChannelStatusIndicator } from './ChannelStatusIndicator';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';

export interface ChannelCardProps {
  channel: {
    channel: string;
    name: string;
    isConnected: boolean;
  };
  binding?: {
    syncStatus: 'pending' | 'synced' | 'error' | 'never_synced' | 'syncing';
    lastSyncedAt?: Date | string;
    externalCatalogId?: string;
  };
  onPress?: (channel: string) => void;
  onBind?: (channel: string) => void;
  onUnbind?: (channel: string) => void;
  containerStyle?: any;
}

const getChannelIcon = (channel: string): string => {
  switch (channel.toLowerCase()) {
    case 'whatsapp':
      return 'logo-whatsapp';
    case 'facebook':
      return 'logo-facebook';
    case 'telegram':
      return 'paper-plane';
    case 'instagram':
      return 'logo-instagram';
    case 'snapchat':
      return 'camera';
    case 'linkedin':
      return 'logo-linkedin';
    default:
      return 'globe-outline';
  }
};

const getChannelColor = (channel: string): string => {
  switch (channel.toLowerCase()) {
    case 'whatsapp':
      return '#25D366';
    case 'facebook':
      return '#0084FF';
    case 'telegram':
      return '#0088CC';
    case 'instagram':
      return '#E4405F';
    case 'snapchat':
      return '#FFFC00';
    case 'linkedin':
      return '#0077B5';
    default:
      return colors.primary;
  }
};

export const ChannelCard: React.FC<ChannelCardProps> = ({
  channel,
  binding,
  onPress,
  onBind,
  onUnbind,
  containerStyle,
}) => {
  const styles = useThemedStyles(getStyles);
  const channelIcon = getChannelIcon(channel.channel);
  const channelColor = getChannelColor(channel.channel);
  const isBound = !!binding;

  const handlePress = () => {
    if (onPress) {
      onPress(channel.channel);
    }
  };

  const handleAction = (e: any) => {
    e.stopPropagation();
    if (isBound && onUnbind) {
      onUnbind(channel.channel);
    } else if (!isBound && onBind) {
      onBind(channel.channel);
    }
  };

  return (
    <TouchableOpacity
      style={[styles.container, containerStyle]}
      onPress={handlePress}
      activeOpacity={0.7}
    >
      <View style={styles.content}>
        <View style={[styles.iconContainer, { backgroundColor: channelColor + '20' }]}>
          <Ionicons name={channelIcon as any} size={24} color={channelColor} />
        </View>
        
        <View style={styles.infoContainer}>
          <View style={styles.headerRow}>
            <Text style={styles.name}>{channel.name}</Text>
            {!channel.isConnected && (
              <View style={styles.notConnectedBadge}>
                <Text style={styles.notConnectedText}>Not Connected</Text>
              </View>
            )}
          </View>
          
          {isBound && binding && (
            <View style={styles.statusRow}>
              <ChannelStatusIndicator status={binding.syncStatus} size="small" />
              {binding.lastSyncedAt && (
                <Text style={styles.lastSyncText}>
                  Last synced: {new Date(binding.lastSyncedAt).toLocaleDateString()}
                </Text>
              )}
            </View>
          )}
        </View>

        <View style={styles.actionsContainer}>
          {channel.isConnected ? (
            <TouchableOpacity
              style={[styles.actionButton, isBound ? styles.unbindButton : styles.bindButton]}
              onPress={handleAction}
              activeOpacity={0.7}
            >
              <Ionicons 
                name={isBound ? 'close-circle' : 'add-circle'} 
                size={20} 
                color={isBound ? colors.error : colors.primary} 
              />
              <Text style={[styles.actionText, { color: isBound ? colors.error : colors.primary }]}>
                {isBound ? 'Unbind' : 'Bind'}
              </Text>
            </TouchableOpacity>
          ) : (
            <View style={styles.disabledButton}>
              <Ionicons name="lock-closed" size={18} color={colors.textLight} />
              <Text style={styles.disabledText}>Connect First</Text>
            </View>
          )}
        </View>
      </View>
    </TouchableOpacity>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    overflow: 'hidden',
    marginBottom: spacing.sm,
    ...designTokens.shadows.sm,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    gap: spacing.sm,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: designTokens.borderRadius.md,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  infoContainer: {
    flex: 1,
    minWidth: 0,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xs / 2,
  },
  name: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  notConnectedBadge: {
    backgroundColor: colors.lightGray,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.xs,
  },
  notConnectedText: {
    fontSize: typography.fontSize.xs - 1,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.xs / 2,
  },
  lastSyncText: {
    fontSize: typography.fontSize.xs - 1,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  actionsContainer: {
    flexShrink: 0,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.sm,
    borderWidth: 1,
  },
  bindButton: {
    borderColor: colors.primary,
    backgroundColor: withAlpha(colors.primary, 0.06),
  },
  unbindButton: {
    borderColor: colors.error,
    backgroundColor: withAlpha(colors.error, 0.06),
  },
  actionText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
    fontWeight: '500',
  },
  disabledButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.sm,
    backgroundColor: colors.lightGray,
    opacity: 0.6,
  },
  disabledText: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
});

